import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { OrderComment } from 'src/app/interfaces/orders';
import { DataServerService } from 'src/app/services/api/data-server.service';
import { AlertService } from 'src/app/services/base/alert.service';
import { LoginService } from 'src/app/services/auth/login.service';

@Component({
  selector: 'app-order-comments',
  templateUrl: './order-comments.component.html',
  styleUrls: ['./order-comments.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderCommentsComponent implements OnInit {
  @Input() orderId!: string;
  @Input() comments: OrderComment[] = [];
  @Input() orderState?: number;
  /** Solange der Auftrag noch nicht mit dem Server synchronisiert wurde (sync !== true),
   * existiert serverseitig noch kein Order-Dokument — POST /order-comment-add würde
   * dann mit 404 fehlschlagen. */
  @Input() orderSynced = true;
  @Output() commentAdded = new EventEmitter<void>();

  newCommentText = '';
  sending = false;
  currentUserId: string = '';

  constructor(
    private dataServer: DataServerService,
    private alert: AlertService,
    private login: LoginService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const token = this.login.decodedToken;
    this.currentUserId = String(token?.id || '');
  }

  async sendComment() {
    if (!this.orderSynced) {
      this.alert.presentToast('Bitte Auftrag zuerst speichern/synchronisieren');
      return;
    }

    const text = this.newCommentText.trim();
    if (!text) {
      this.alert.presentToast('Bitte Text eingeben');
      return;
    }

    this.sending = true;
    this.cdr.markForCheck();

    try {
      const res: any = await this.dataServer.addOrderComment(this.orderId, text, false);
      if (res?.status === 200 && res?.data?.success) {
        // Add comment to local array immediately — der Server liefert createdBy nur als
        // rohe ID (kein Populate), daher hier direkt mit den bekannten eigenen Daten ersetzen.
        if (res.data.comment) {
          const token = this.login.decodedToken;
          const comment = {
            ...res.data.comment,
            createdBy: token ? { id: String(token.id), email: token.email } : res.data.comment.createdBy,
          };
          this.comments = [...this.comments, comment];
        }
        this.newCommentText = '';
        this.alert.presentToast('Notiz hinzugefügt');
        this.commentAdded.emit();
      } else {
        this.alert.presentToast('Fehler beim Hinzufügen der Notiz');
      }
    } catch (e) {
      console.error('sendComment error:', e);
      this.alert.presentToast('Fehler beim Hinzufügen der Notiz');
    }

    this.sending = false;
    this.cdr.markForCheck();
  }

  getUserName(createdBy: any): string {
    if (!createdBy) return 'Unbekannt';
    if (typeof createdBy === 'string') {
      // Keine Populate-Daten vorhanden (nur die rohe User-ID) — bei sich selbst kennen
      // wir die E-Mail aus dem Token, ansonsten lieber "Unbekannt" als eine ID anzeigen.
      if (createdBy === this.currentUserId && this.login.decodedToken?.email) {
        return this.login.decodedToken.email;
      }
      return 'Unbekannt';
    }
    if (createdBy.username) return createdBy.username;
    if (createdBy.email) return createdBy.email;
    return 'Unbekannt';
  }

  isOwnComment(comment: OrderComment): boolean {
    if (!comment.createdBy) return false;
    if (typeof comment.createdBy === 'string') {
      return comment.createdBy === this.currentUserId;
    }
    return comment.createdBy.id === this.currentUserId;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
